import { FC } from "react";
import { ClipboardCheckIcon } from "lucide-react-native";

import { VnBox } from "@/components/VnBox";
import { VnCard } from "@/components/VnCard";
import { VnIcon } from "@/components/VnIcon";
import { VnLink } from "@/components/VnLink";
import { VnText } from "@/components/VnText/VnText";
import { useGetHouseholdQuery } from "@/data/queries/householdsQueries";
import { EligibilityCardStateType } from "@/domain/dashboardCards";

import { DashboardCompactCard } from "./DashboardCompactCard";

// Resumes the eligibility flow at the step after the last completed one.
const getNextEligibilityStepLink = (
  eligibilityLastStep: string | null | undefined,
) => {
  switch (eligibilityLastStep) {
    case "MARITAL_STATUS":
      return "/eligibility/live-care-children";
    case "LIVE_CARE_CHILDREN":
      return "/eligibility/parent-guardian-student";
    case "PARENT_GUARDIAN_STUDENT":
      return "/eligibility/support-disabled-individual";
    case "SUPPORT_DISABLED_INDIVIDUAL":
      return "/eligibility/support-other-relatives";
    case "SUPPORT_OTHER_RELATIVES":
      return "/eligibility/household-income";
    case "SOMEONE_SUPPORTS_YOU":
      return "/eligibility/someone-supports-you";
    case "HOUSEHOLD_INCOME":
      return "/eligibility/special-conditions";
    default:
      return "/eligibility/marital-status";
  }
};

type EligibilityNextStepCardPropsType = {
  state: EligibilityCardStateType;
  isPrimary: boolean;
};

export const EligibilityNextStepCard: FC<EligibilityNextStepCardPropsType> = ({
  state,
  isPrimary,
}) => {
  const { data: household } = useGetHouseholdQuery();

  const isInProgress = state === "IN_PROGRESS";
  const link = getNextEligibilityStepLink(household?.eligibilityLastStep);

  if (!isPrimary) {
    return (
      <DashboardCompactCard
        i18nKey={
          isInProgress
            ? "native:homeScreen.eligibilityCardCompactStarted"
            : "native:homeScreen.eligibilityCardCompactNotStarted"
        }
        actions={
          <VnLink
            href={link}
            renderAsButton
            i18nKey="native:homeScreen.checkEligibilityCompact"
            buttonProps={{ variant: "outline" }}
          />
        }
      />
    );
  }

  const messageI18nKey = isInProgress
    ? "native:homeScreen.eligibilityCardStarted"
    : "native:homeScreen.eligibilityCardNotStarted";

  return (
    <VnCard className="flex flex-col">
      <VnBox className="flex flex-row items-center gap-4">
        <VnBox className="rounded-md bg-black/5 p-6">
          <VnIcon as={ClipboardCheckIcon} size={42} strokeWidth={1} />
        </VnBox>

        <VnText variant="lg" i18nKey={messageI18nKey} />
      </VnBox>

      <VnLink
        href={link}
        renderAsButton
        i18nKey="native:homeScreen.checkEligibility"
      />
    </VnCard>
  );
};
